<script>
	const uaParser = new (require('ua-parser-js'))();
	const inputValidationMixin = require('mixins/input-validation-mixin');
	const apiDefinitions = require('../../../../../lib/services/api/core/apiDefinitions');
	const _ = require('underscore');
	const { getWordCount } = require('utilities');
	const Store = require('kvstore');

	let browser = uaParser.getBrowser();
	let isSafari = browser && browser.name ? browser.name.toLowerCase().indexOf('safari') !== -1 : false;
	let isIE = browser && browser.name ? browser.name.toLowerCase() === 'ie' : false;

	module.exports = {
		components: {
			message: require('components/message.vue').default
		},
		mixins: [inputValidationMixin],
		props: {
			value: {
				required: true
			},
			placeholder: {
				type: String
			},
			maximumCharacterCount: {
				default: apiDefinitions.validation.max,
				type: Number
			},
			characterLimitWarningThreshold: {
				default: 0.5,
				type: Number
			},
			rows: {
				required: false
			},
			cols: {
				required: false
			},
			maxlength: {
				default: apiDefinitions.validation.max
			},
			readonly: {
				default: false
			},
			disabled: {
				default: false
			},
			required: {
				default: false
			},
			lazy: {
				default: false
			},
			hideMessageBar: {
				default: false
			},
			maxWords: {
				default: apiDefinitions.validation.max,
				type: Number
			},
			wordLimitWarningThreshold: {
				default: 0.8,
				type: Number
			},
			minWords: {
				default: 0,
				type: Number
			}
		},
		data() {
			return {
				focusing: false,
				hasHadFocus: false,
				guid: Store.inc('validatedTextareaGuid')
			};
		},
		computed: {
			characterLimitWarning() {
				let maximumCharacterCount = this.maximumCharacterCount;
				let charactersRemaining = this.charactersRemaining;
				let characterLimitWarningThreshold = this.characterLimitWarningThreshold;
				let characterLimitError = this.characterLimitError;

				return (
					!characterLimitError && typeof charactersRemaining === 'number' && charactersRemaining <= characterLimitWarningThreshold * maximumCharacterCount
				);
			},
			characterLimitError() {
				let charactersRemaining = this.charactersRemaining;

				return typeof charactersRemaining === 'number' && charactersRemaining < 0;
			},
			charactersRemaining() {
				let maximumCharacterCount = this.maximumCharacterCount;
				let value = this.value;
				let length = 0;
				if (!value) {
					return maximumCharacterCount;
				} else {
					length = value.length;
				}

				// Safari counts new lines twice towards maxlength
				if (isSafari || isIE) {
					let newLineCount = value.split(/\n/).length - 1;
					length += newLineCount;
				}

				return maximumCharacterCount - length;
			},
			wordsRemaining() {
				const maxWords = this.maxWords;
				const wordCount = getWordCount(this.value || '');

				return maxWords - wordCount;
			},
			wordLimitError() {
				return this.wordsRemaining < 0;
			},
			wordLimitWarning() {
				const maxWords = this.maxWords;
				const wordsRemaining = this.wordsRemaining;
				const limitThreshold = this.wordLimitWarningThreshold;
				const wordLimitError = this.wordLimitError;

				return !wordLimitError && wordsRemaining <= limitThreshold * maxWords;
			},
			wordsNeeded() {
				const minWords = this.minWords;
				const wordCount = getWordCount(this.value || '');

				return minWords - wordCount;
			},
			statusMessage() {
				const maxWords = this.maxWords;
				const wordsRemaining = this.wordsRemaining;

				const maximumCharacterCount = this.maximumCharacterCount;
				const charactersRemaining = this.charactersRemaining;

				const messages = [
					{
						type: 'error',
						message: `Maximum of ${maxWords} words. (${-1 * wordsRemaining} too many)`,
						condition: this.wordLimitError
					},
					{
						type: 'warning',
						message: `Maximum of ${maxWords} words. (${wordsRemaining} remaining)`,
						condition: this.wordLimitWarning
					},
					{
						type: 'error',
						message: `Maximum of ${maximumCharacterCount} characters. (${-1 * charactersRemaining} too many)`,
						condition: this.characterLimitError
					},
					{
						type: 'warning',
						message: `Maximum of ${maximumCharacterCount} characters. (${charactersRemaining} remaining)`,
						condition: this.characterLimitWarning
					},
					{
						type: 'warning',
						message: `Please write at least ${this.minWords} words. (${this.wordsNeeded} remaining)`,
						condition: this.hasHadFocus && this.wordsNeeded > 0
					}
				];

				return messages.find(message => message.condition);
			}
		},
		methods: {
			onInput: _.debounce(function () {
				// Prioritize limits over other errors.
				if (this && !(this.characterLimitError || this.wordLimitError)) {
					this.checkValidity();
				}
			}, 500),
			focus() {
				let el = this.$refs.textArea;
				if (el && el.focus) {
					el.focus();
				}
			},
			onLimitError(error, type) {
				let customValidityMsg = '';
				if (error) {
					customValidityMsg = `${type} limit exceeded.`;
				}
				this.setCustomValidity(customValidityMsg);
			}
		},
		watch: {
			characterLimitError(cle) {
				this.onLimitError(cle, 'Character');
			},
			wordLimitError(error) {
				this.onLimitError(error, 'Word');
			}
		},
		mounted() {
			// Expose text area to input-validation-mixin
			this.customEl = this.$refs.textArea;

			if (this.value) {
				this.hasHadFocus = true;
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'core/spacing';

	.validated-textarea {
		position: relative;

		&.textarea-warn {
			textarea:focus {
				border-color: $c-warning !important;
			}
		}

		&.textarea-error {
			textarea {
				border-color: $c-red !important;
			}
		}

		textarea {
			width: 100%;
			height: 100%;
			resize: vertical;
			appearance: none;
			border-radius: 0;
			border: 1px solid $c-grey;
			padding: 0.5rem;

			&:focus {
				border-color: $c-blue;
			}
		}

		.placeholder {
			display: none;
			position: absolute;
			padding: 0.5rem;
			color: $c-slate-dark;
			height: 0;
		}

		.placeholder.show {
			display: block;
		}
	}
</style>
<template>
	<div
		class="validated-textarea"
		:class="{
			'textarea-warn': characterLimitWarning || wordLimitWarning,
			'textarea-error': characterLimitError || wordLimitError
		}">
		<div
			v-if="placeholder"
			@click="focus"
			class="placeholder"
			:class="{ show: !value && !focusing }"
			v-html="placeholder"></div>
		<textarea
			ref="textArea"
			class="validated-input input margin-bottom-medium"
			:class="{
				touched: touched
			}"
			:rows="rows"
			:cols="cols"
			:readonly="readonly"
			:disabled="disabled"
			:required="required"
			:lazy="lazy"
			v-model="value"
			:maxlength="maxlength"
			:aria-describedby="`validated-textarea-message-${guid}`"
			@input="onInput($event)"
			@change="onInput($event)"
			@focus="focusing = true"
			@blur="
				focusing = false;
				hasHadFocus = true;
			"></textarea>

		<message
			v-if="statusMessage && !hideMessageBar"
			class="validated-textarea-message"
			:id="`validated-textarea-message-${guid}`"
			:type="statusMessage.type">
			{{ statusMessage.message }}
		</message>
	</div>
</template>
